import {CreateAccessor} from "mobx-graphlink";
import {RunInAction_Set, TextSpeaker, TimeToString} from "web-vcore";
import {Assert, Clone} from "js-vextensions";
import {Text} from "react-vcomponents";
import {EngineConfig_DreamTranscribe, TranscribeTarget} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_DreamTranscribe.js";
import {JournalEntry, JournalSegment} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {store} from "../../../Store/index.js";
import {LogType} from "../../../UI/Tools/@Shared/LogEntry.js";
import {InAndroid, nativeBridge} from "../../../Utils/Bridge/Bridge_Native.js";
import {BaseSpanEffects, ExtractBracketedTextFromSpanText, ProcessTranscribedSpans, SpanEffect_NearTTS, SpansToText, StripBracketedTextFromSpanText, TranscribedSpan, TranscriptionChunkInfo, WhisperTranscriptionWatcher} from "../../../Utils/Bridge/Whisper.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {AlarmsComp, PhaseChange_ExtraMessage} from "./AlarmsComp.js";
import {NarrateText_ForEngineComp, NarrateText_StopAll} from "../../../Utils/Services/TTS.js";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import React from "react";
import {Journey_GetSessionPeriodsToShow} from "../../../Server/DB/journalEntries.js";
import {MicroSnoozeComp} from "./MicroSnoozeComp.js";
import {AdvancedTranscriptionHandler} from "./AdvancedTranscriptionHandler.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export class DreamTranscribeComp extends EngineSessionComp<EngineConfig_DreamTranscribe> {
	constructor(session: LiveSession, config: EngineConfig_DreamTranscribe) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	override GetTriggerPackages() {
		return [
			new TriggerPackage("DreamTranscribe_StartForceEnable", this.c.startTranscribeForceEnable_triggerSet, this, {}, triggerInfo=>{
				this.StartTranscribeForceEnable();
			}),
			new TriggerPackage("DreamTranscribe_StopForceEnable", this.c.stopTranscribeForceEnable_triggerSet, this, {}, triggerInfo=>{
				this.StopTranscribeForceEnable();
			}),
		];
	}

	transcribeForceEnabled = false;
	StartTranscribeForceEnable() {
		if (this.transcribeForceEnabled) return;
		this.transcribeForceEnabled = true;
		//NarrateText_ForEngineComp(this, `Starting forced transcription`, this.s.c.general.defaultVoice_volumeMultiplier);
		this.UpdateTranscribeEnabled();
	}
	StopTranscribeForceEnable() {
		if (!this.transcribeForceEnabled) return;
		this.transcribeForceEnabled = false;
		//NarrateText_ForEngineComp(this, `Stopping forced transcription`, this.s.c.general.defaultVoice_volumeMultiplier);
		this.UpdateTranscribeEnabled();
	}

	async NarrateText(text: string, volumeMultiplier = 1) {
		await NarrateText_ForEngineComp(this, text, this.s.c.general.defaultVoice_volumeMultiplier * volumeMultiplier);
	}

	cycle_transcribedSpansForSolver = [] as TranscribedSpan[];
	override OnLeavePhase_Drifting(newPhase: Phase): void {
		this.cycle_transcribedSpansForSolver.Clear();
	}
	override OnStop() {
		NarrateText_StopAll();
		this.transcriptionWatcher.UnregisterAfterTranscribedToNow();
	}

	override OnStartPhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage): void {
		this.UpdateTranscribeEnabled();
	}
	override Unsuspend() {
		const result = super.Unsuspend();
		this.UpdateTranscribeEnabled();
		return result;
	}
	override Suspend() {
		const result = super.Suspend();
		this.UpdateTranscribeEnabled();
		return result;
	}

	UpdateTranscribeEnabled() {
		if (!this.c.enabled) return; // this func can be called externally (eg. from MicroSnoozeComp), so make sure this comp is actually enabled
		const baseEnabled = (()=>{
			// hidden/inferrable extra condition: base-enabled is false when comp is suspended
			if (this.IsSuspended()) return false;

			if (this.c.transcribeBaseEnabledIf_microSnoozeActive && this.s.Comp(MicroSnoozeComp).snooze_active) return true;
			if (this.c.transcribeBaseEnabledIf_inPhases?.includes(this.s.Comp(AlarmsComp).phase)) return true;
			return false;
		})();
		const newRegistrationState = this.transcribeForceEnabled || baseEnabled;
		if (newRegistrationState && !this.transcriptionWatcher.IsRegisteredAndNotUnregistering()) {
			this.transcriptionWatcher.Register();
		} else if (!newRegistrationState && this.transcriptionWatcher.IsRegisteredAndNotUnregistering()) {
			this.transcriptionWatcher.UnregisterAfterTranscribedToNow();
		}
	}

	GetCurrentTranscriptionTarget() {
		if (this.behaviorEnabled) {
			const {journalEntry, transcribeTargetSegment} = GetKeySegmentsInCurrentDream();

			const transcribeTarget_fromJourneyComp = this.c.transcribeTarget;
			const transcribeTarget_fromJourneyComp_invalid = transcribeTarget_fromJourneyComp == TranscribeTarget.dreamSegment && (journalEntry == null || transcribeTargetSegment == null);
			if (!transcribeTarget_fromJourneyComp_invalid) {
				return transcribeTarget_fromJourneyComp;
			}
		}
		return TranscribeTarget.panel;
	}
	advancedTranscriptionHandler = new AdvancedTranscriptionHandler({
		getTranscribeTarget: ()=>this.GetCurrentTranscriptionTarget(),
		getConfig: ()=>this.c,
		narrateText: text=>this.NarrateText(text),
	});
	transcriptionWatcher = new WhisperTranscriptionWatcher({
		keepsRecordingAlive: true,
		onChunkTranscribed: info=>{
			this.advancedTranscriptionHandler.HandleChunk(info);

			if (this.c.solver_enabled && this.s.Comp(AlarmsComp).DoesPhaseSupportSolveProgress()) {
				const spans_forSolver = ProcessTranscribedSpans(info.spans, [
					...BaseSpanEffects(),
					// use whole-span removal; better to err on over-rejection than under-rejection (else user could cheat and use dream-quiz option-switching to fulfill requirements)
					new SpanEffect_NearTTS({maxDist: this.c.solver_ignoreWordsNearTTS * 1000, remove_wholeSpan: true}),
				]);
				this.cycle_transcribedSpansForSolver.push(...spans_forSolver);
				this.TryToSolve(spans_forSolver);
			}
		},
	});

	TryToSolve(newSpans: TranscribedSpan[]) {
		const fullText = SpansToText(this.cycle_transcribedSpansForSolver);
		const wordsTranscribed = TextToWordsOrTargets(fullText, false);

		const alarmsComp = this.s.Comp(AlarmsComp);
		const success = ()=>{
			alarmsComp.NotifySolveCompleted();
			this.cycle_transcribedSpansForSolver.Clear();
			return true;
		};
		const fail = ()=>{
			if (this.c.solver_markProgressBeforeCompletion) {
				//this.Log(`User transcribed text, but it didn't match the requirements. @transcribedText:"${fullText}"`, LogType.Event_Large);
				const newSpans_text = SpansToText(newSpans);
				// if new-spans had at least one word, consider it an "attempt at solving" the current alarms
				if (TextToWordsOrTargets(newSpans_text, false).length > 0) {
					alarmsComp.NotifySolveProgress(this.c.solver_maxTimeForProgress);
				}
			}
			return false;
		};

		// check basic transcribed-word-count requirement
		if (wordsTranscribed.length < this.c.solver_minWords) {
			return fail();
		}

		// check if the user-defined required word-sequence is found
		if (this.c.solver_wordSequence.startsWith("/") && this.c.solver_wordSequence.endsWith("/")) {
			const regex = new RegExp(this.c.solver_wordSequence.slice(1, -1), "i");
			if (!regex.test(fullText)) {
				return fail();
			}
		} else {
			const targetsToFind = TextToWordsOrTargets(this.c.solver_wordSequence, true);
			let wordsTranscribed_stillInPool = wordsTranscribed.slice();
			for (const target of targetsToFind) {
				let indexAfterMatchInPool = -1;
				for (let i = 0; i < wordsTranscribed_stillInPool.length; i++) {
					const wordInPool = wordsTranscribed_stillInPool[i];
					if (wordInPool == target) {
						indexAfterMatchInPool = i + 1;
						break;
					}
					// handling for groups (ie. where a transcribed word only has to match one of a set of word-alternatives)
					if (target.includes("|")) {
						const targetOptions = target.split("|").map(a=>a.trim());
						const matchingTargetOpt = targetOptions.find(targetOpt=>{
							// special handling for multi-word target-options
							if (targetOpt.includes(" ")) {
								return wordsTranscribed_stillInPool.slice(i).join(" ").startsWith(targetOpt);
							}
							return wordInPool == targetOpt;
						});
						if (matchingTargetOpt) {
							// if matching target-opt is multi-word, move past all words in that sub-sequence
							indexAfterMatchInPool = i + matchingTargetOpt.split(" ").length;
							break;
						}
					}
				}
				if (indexAfterMatchInPool == -1) {
					return fail();
				}
				wordsTranscribed_stillInPool = wordsTranscribed_stillInPool.slice(indexAfterMatchInPool);
			}
		}

		// check if the user-defined required transcription time is met (even after max-transcription-time-counted-per-word adjustments)
		const transcribeTimeRequired = this.c.solver_minTime * 1000;
		const transcribeTimeCountedPerSpan = this.cycle_transcribedSpansForSolver.map(span=>{
			const wordsInSpan = TextToWordsOrTargets(span.text, false).length;
			const maxTranscribeTimePossibleForWords = wordsInSpan * this.c.solver_maxTimeValuePerWord * 1000;
			return (span.end - span.start).KeepAtMost(maxTranscribeTimePossibleForWords);
		});
		const totalTranscribeTimeCounted = transcribeTimeCountedPerSpan.Sum();
		//console.log([totalTranscribeTimeCounted, transcribeTimeRequired]);
		if (totalTranscribeTimeCounted < transcribeTimeRequired) {
			return fail();
		}

		return success();
	}
}

export const GetKeySegmentsInCurrentDream = CreateAccessor((): {journalEntry: JournalEntry|n, lastEndedDreamSegment: JournalSegment|n, transcribeTargetSegment: JournalSegment|n}=>{
	const {journalEntryForLiveSession} = Journey_GetSessionPeriodsToShow();
	if (journalEntryForLiveSession == null) return {journalEntry: null, lastEndedDreamSegment: null, transcribeTargetSegment: null};

	let lastEndedDreamSegment: JournalSegment|n;
	for (let i = 0; i < journalEntryForLiveSession.segments.length; i++) {
		const segment = journalEntryForLiveSession.segments[i];
		const prevSegment = journalEntryForLiveSession.segments[i - 1] as JournalSegment|n;
		// if we reach an "awake" segment, and previous was a dream-segment, then mark previous as the "last ended dream segment" (so far)
		if (segment.wakeTime != null && prevSegment?.wakeTime == null) {
			lastEndedDreamSegment = prevSegment;
		}
	}
	const transcribeTargetSegment = lastEndedDreamSegment ?? journalEntryForLiveSession.segments.filter(a=>a.wakeTime == null).LastOrX();
	return {journalEntry: journalEntryForLiveSession, lastEndedDreamSegment, transcribeTargetSegment};
});

// todo: maybe remove this, and use GetWordsInText_X in StringToWords.ts instead?
export function TextToWordsOrTargets(text: string, allowGroups: boolean) {
	let text_simplified = text.toLowerCase();
	// only alphanumerics + apostrophes + group-specifying-chars are kept; the rest all get replaced with spaces
	text_simplified = text_simplified.replace(/[^a-z0-9'()|]/g, " ");
	if (!allowGroups) text_simplified = text_simplified.replace(/[()|]/g, " ");
	text_simplified = text_simplified.replace(/ +/g, " ").trim();

	const resultWords = [] as string[];
	for (let i = 0; i < text_simplified.length; i++) {
		// skip standalone spaces
		if (text_simplified[i] == " ") {
			continue;
		}
		// special group-handling
		else if (text_simplified[i] == "(") {
			const groupEnder_charIndex = text_simplified.indexOf(")", i);
			if (groupEnder_charIndex != -1) {
				const groupText = text_simplified.slice(i + 1, groupEnder_charIndex);
				resultWords.push(groupText);
				// increase "i", so that next char processed is after the group
				i = groupEnder_charIndex;
				continue;
			}
		}
		// regular word-handling of seeking till next space
		else {
			const nextSpace_charIndex = text_simplified.indexOf(" ", i);
			const postWordCharIndex = nextSpace_charIndex == -1 ? text_simplified.length : nextSpace_charIndex;
			const word = text_simplified.slice(i, postWordCharIndex);
			resultWords.push(word);
			// increase "i", so that next char processed is after the word
			i = postWordCharIndex - 1; // (one less, since loop will increase it by 1 -- thus resuming at first post-word char)
		}
	}
	return resultWords;
};